import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Separator } from "../../ui/separator";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Skeleton } from "../../ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../ui/alert-dialog";
import { ArrowLeft, Calendar, Clock, Users, MapPin, Building2, Mail, Phone, CreditCard, Send, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import type { CateringOrder, CateringOrderStatus } from "../../../hooks/useCateringOrders";
import { ecommerceapiUrl, clientheaderconfig } from "../../Helpers/Config";
import { addressFormat, encodeValue } from "../../Helpers/SettingHelper";
import axios from "axios";

const statusColors: Record<string, string> = {
  enquiry: "bg-muted text-muted-foreground",
  pending_payment: "bg-yellow-100 text-yellow-800",
  paid: "bg-blue-100 text-blue-800",
  confirmed: "bg-accent/20 text-accent-foreground",
  in_progress: "bg-purple-100 text-purple-800",
  completed: "bg-accent/20 text-accent-foreground",
  cancelled: "bg-destructive/20 text-destructive",
};

const statusOptions = [
  { value: "enquiry", label: "Enquiry" },
  { value: "pending_payment", label: "Pending Payment" },
  { value: "paid", label: "Paid" },
  { value: "confirmed", label: "Confirmed" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const venueLabels: Record<string, string> = {
  in_house: "In-House Dining",
  private_dining: "Private Dining",
  external: "External Venue",
};

interface OrderMenuItem {
  menu_item_id: string;
  category_id: string;
  category_name: string;
  item_name: string;
  image_url?: string | null;
  price_per_pax: number | null;
}
interface apiResponse {
  success: boolean;
  message?: string;
}

export default function AdminCateringOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<CateringOrder | null>(null);
  const [orderUpgrades, setOrderUpgrades] = useState<{ name: string; quantity: number; price: number }[]>([]);
  const [orderAddons, setOrderAddons] = useState<{ name: string; quantity: number; price: number }[]>([]);
  const [menuSelections, setMenuSelections] = useState<{ categoryName: string; items: string[] }[]>([]);
  const [loading, setLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState("");
  const [isSendingLink, setIsSendingLink] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [id]);
  const fetchOrder = async () => {
      if (!id) return;

      try {
        const { data: orderDetails } = await axios.get(`${ecommerceapiUrl}catering/orderdetail/${encodeValue(id)}`, clientheaderconfig);
        if (orderDetails.status === "ok") {
          const result = orderDetails.result;

          setOrder(orderDetails.result);

          const orderComboSet = result?.orderComboSet || [];
          setMenuSelections(orderComboSet);

          const orderUpgrade = result?.orderUpgrade || [];
          setOrderUpgrades(orderUpgrade);

          const orderAddons = result?.orderAddons || [];
          setOrderAddons(orderAddons);

          const orderAdminNotes = result?.orderAdminNotes || '';          
          setAdminNotes(orderAdminNotes)
        }
      } catch (err) {
        console.error("Failed to fetch order:", err);
        toast.error("Failed to load order");
      } finally {
        setLoading(false);
      }
    };

  const handleStatusChange = async (newStatus: CateringOrderStatus) => {
    if (!order) return;

    try {
      const postData = {
        orderStatus:newStatus
      }
      const response = await axios.put<apiResponse>(`${ecommerceapiUrl}catering/changeStatus/${encodeValue(id)}`, postData, clientheaderconfig);
      fetchOrder();
      toast.success("Status updated");
    } catch (err) {
      console.error("Failed to update status:", err);
      toast.error("Failed to update status");
    }
  };

  const handleSaveNotes = async () => {
    if (!order) return;

    try {
      const postData = {
        orderRemarks:adminNotes
      }
      const response = await axios.put<apiResponse>(`${ecommerceapiUrl}catering/updateOrderRemarks/${encodeValue(id)}`, postData, clientheaderconfig);
      toast.success("Notes saved");
    } catch (err) {
      console.error("Failed to save notes:", err);
      toast.error("Failed to save notes");
    }
  };

  const handleSendPaymentLink = async (amount?: number) => {
    if (!order) return;

    setIsSendingLink(true);
    try {
      /*  const { data, error } = await supabase.functions.invoke("catering-payment-link", {
        body: { orderId: order.id, amount },
      });

      if (error) throw error;

      toast.success(
        <div>
          Payment link created!
          <br />
          <a href={data.url} target="_blank" rel="noopener noreferrer" className="underline">
            {data.url}
          </a>
        </div>,
      ); */
    } catch (err) {
      console.error("Failed to create payment link:", err);
      toast.error("Failed to create payment link");
    } finally {
      setIsSendingLink(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 py-8">
          <div className="container max-w-4xl">
            <Skeleton className="h-8 w-32 mb-6" />
            <Skeleton className="h-64 w-full" />
          </div>
        </main>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-semibold mb-4">Order Not Found</h1>
            <Button onClick={() => navigate("/clientpanel/catering/orders")}>Back to Orders</Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <main className="flex-1 py-8">
        <div className="container max-w-4xl">
          <Button variant="ghost" onClick={() => navigate("/clientpanel/catering/orders")} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Orders
          </Button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold">Order #{order.orderNumber}</h1>
              <p className="text-muted-foreground">Created {format(new Date(order.createdAt), "PPP 'at' p")}</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={order.orderStatus} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-6">
            {/* Event & Menu Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Event Details</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{format(new Date(order.orderDate), "PPP")}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Time</p>
                      <p className="font-medium">{order.orderTime}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Guests</p>
                      <p className="font-medium">{order.orderPax} pax</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Venue</p>
                      <p className="font-medium">{venueLabels[order.orderVenueType]}</p>
                    </div>
                  </div>
                </div>
                {order.orderDeliveryAddress!=="" && order.orderDeliveryAddress!==null &&
                 <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Delivery Address</p>
                      <p className="font-medium">{addressFormat(order.orderDeliveryUnitNumber, order.orderDeliveryFloorNumber, order.orderDeliveryAddress, order.orderDeliveryPostalCode, '')}</p>
                    </div>
                  </div>
}

                <Separator />

                <div>
                  <p className="text-sm text-muted-foreground">Menu</p>
                  <p className="font-medium text-lg">{order.orderProductName}</p>
                </div>

                {order.orderIsCorporate && (
                  <>
                    <Separator />
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Company</p>
                        <p className="font-medium">{order.orderCompanyName}</p>
                        <p className="font-medium">{order.orderCompanyAddress}</p>
                      </div>
                    </div>
                  </>
                )}

                {order.orderAdditionalInformation && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm text-muted-foreground">Special Requests</p>
                      <p className="mt-1">{order.orderAdditionalInformation}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Menu Selections, Upgrades & Add-ons */}
            {(menuSelections.length > 0 || orderUpgrades.length > 0 || orderAddons.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Menu Selections</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {menuSelections?.length > 0 && (
                    <div className="space-y-3">
                      {Object.entries(
                        menuSelections.reduce<Record<string, OrderMenuItem[]>>((acc: any, item: any) => {
                          (acc[item.category_name] ??= []).push(item);
                          return acc;
                        }, {}),
                      ).map(([categoryName, items]) => (
                        <div key={categoryName} className="space-y-2">
                          <h4 className="font-medium text-sm">{categoryName}</h4>

                          <ul className="mt-1 space-y-0.5">
                            {items.map((item) => (
                              <li key={item.menu_item_id} className="flex items-center gap-2 text-sm">
                                <span className="flex-1">{item.item_name}</span>

                                {item.price_per_pax && item.price_per_pax > 0 ? (
                                  <Badge variant="outline" className="text-xs border-accent text-accent">
                                    +${item.price_per_pax.toFixed(2)}/pax
                                  </Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground">Included</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}

                  {orderUpgrades.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-sm font-semibold mb-2">Upgrades</p>
                        <div className="space-y-2">
                          {orderUpgrades.map((u, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>
                                {u.name} {u.quantity > 1 && `×${u.quantity}`}
                              </span>
                              <span className="font-medium">${u.price.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {orderAddons.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-sm font-semibold mb-2">Add-ons</p>
                        <div className="space-y-2">
                          {orderAddons.map((a, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>
                                {a.name} ×{a.quantity}
                              </span>
                              <span className="font-medium">${a.price.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Customer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="font-medium text-lg">{order.ordercustomerName}</p>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${order.orderCustomerEmail}`} className="hover:underline">
                    {order.orderCustomerEmail}
                  </a>
                </div>
                {order.orderCustomerMobile && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <a href={`tel:${order.orderCustomerMobile}`} className="hover:underline">
                      {order.orderCustomerMobile}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Pricing</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Base Price ({order.orderPax} pax)</span>
                    <span>${order.orderBasePrice.toFixed(2)}</span>
                  </div>
                  {order.orderUpgradesTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Upgrades</span>
                      <span>${order.orderUpgradesTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {order.orderAddonsTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Add-ons</span>
                      <span>${order.orderAddonsTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {order.orderVenueSurcharge > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Venue Surcharge</span>
                      <span>${order.orderVenueSurcharge.toFixed(2)}</span>
                    </div>
                  )}
                  {order.orderDeliveryFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Delivery Fee</span>
                      <span>${order.orderDeliveryFee.toFixed(2)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${order.orderSubTotal.toFixed(2)}</span>
                  </div>
                  {order.orderDiscountAmount > 0 && (
                    <div className="flex justify-between text-accent">
                      <span>Discount</span>
                      <span>-${order.orderDiscountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">GST</span>
                    <span>${order.orderTaxAmount.toFixed(2)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span className="text-accent">${order.orderGrandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Mode</p>
                    <p className="font-medium capitalize">{order.orderPaymentMode?.replace("_", " ") || "Not selected"}</p>
                  </div>
                  <Badge className={cn(statusColors[order.orderStatus])}>{order.paid_at ? "Paid" : order.orderStatus === "enquiry" ? "Enquiry" : "Unpaid"}</Badge>
                </div>

                {order.paid_at && (
                  <div>
                    <p className="text-sm text-muted-foreground">Paid At</p>
                    <p className="font-medium">{format(new Date(order.paid_at), "PPP 'at' p")}</p>
                  </div>
                )}

                {/* !order.paid_at && order.orderStatus !== "cancelled" && (
                  <div className="flex gap-3 pt-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" disabled={isSendingLink}>
                          {isSendingLink ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                          Send Full Payment Link
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Send Payment Link?</AlertDialogTitle>
                          <AlertDialogDescription>This will create a Stripe payment link for the full amount (${order.orderGrandTotal.toFixed(2)}) and update the order.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleSendPaymentLink()}>Send Link</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" disabled={isSendingLink}>
                          Send 50% Deposit Link
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Send Deposit Link?</AlertDialogTitle>
                          <AlertDialogDescription>This will create a Stripe payment link for a 50% deposit (${(order.orderGrandTotal / 2).toFixed(2)}).</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleSendPaymentLink(order.orderGrandTotal / 2)}>Send Link</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ) */}
              </CardContent>
            </Card>

            {/* Admin Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Admin Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="adminNotes">Internal notes (not visible to customer)</Label>
                  <Textarea id="adminNotes" value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Add internal notes about this order..." rows={4} className="mt-2" />
                </div>
                <Button onClick={handleSaveNotes}>Save Notes</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
